import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, UserIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useBrandingColors } from '../hooks/useBrandingColors';

const ContactsDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  estaffLinkMissing,
  sourceLabel = 'HH.ru'
}) => {
  const colors = useBrandingColors();
  const confirmDisabled = loading;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                    <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Открыть контактную информацию
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Это платная операция. Будет списан <span className="font-semibold">1 просмотр</span> из вашего пакета {sourceLabel}.
                      </p>
                      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                              Внимание
                            </h3>
                            <div className="mt-2 text-sm text-yellow-700 space-y-2">
                              <p>
                                После подтверждения операция не может быть отменена. Убедитесь, что вы действительно хотите просмотреть контакты этого кандидата.
                              </p>
                              <p className="text-xs">
                                Для просмотра контактов необходим активный доступ к базе резюме {sourceLabel}.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {estaffLinkMissing && (
                        <div className="mt-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-orange-800">E-Staff</h3>
                              <div className="mt-1 text-sm text-orange-700">
                                <p>Для этой позиции не выбрана вакансия E-Staff. Контакты будут открыты в HH, но кандидат не будет сохранён в E-Staff.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-xl border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = colors.primaryHover; } }}
                    onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.backgroundColor = colors.primary; } }}
                    onClick={onConfirm}
                    disabled={confirmDisabled}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Загрузка...
                      </>
                    ) : (
                      <>
                        <UserIcon className="h-4 w-4 mr-2" />
                        Показать контакты
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:col-start-1 sm:mt-0 sm:text-sm"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Отмена
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ContactsDialog;
